'use client';

import React from "react";
import { TasticProps } from "frontastic/tastics/types"
import dynamic from "next/dynamic";
import Markdown from "components/commercetools-ui/organisms/markdown"
import { usePathname } from "next/navigation";

const OurTechnologiesTastic = ({ data }: TasticProps<any>) => {
  // Ensure blogs is always an array
  const pathname = usePathname();
  const pathParts = pathname.split("/").filter(Boolean);   
  const lastPathPart = "/" + pathParts[pathParts.length - 1] + "/"; 

  const blogs = Array.isArray(data?.data?.dataSource) ? data.data.dataSource : [];

  // Filter blogs with URLs containing '/press-releases/'
  const filteredBlogs = blogs.filter((blog: any) => blog.url?.includes(lastPathPart));
//'/renovation-guide/'
  const truncateDescription = (htmlContent: string, maxLength = 150) => {
    // Create a DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
  
    // Traverse child nodes to find the first valid text-containing element
    let textContent = '';
    Array.from(doc.body.childNodes).some((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();
  
        // Check if the element is a valid tag for description (e.g., <p>, <h1>, <h2>, etc.)
        if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
          textContent = element.textContent?.trim() || '';
          return true; // Stop searching once valid text is found
        }
      }
      return false;
    });
  
    // If textContent is empty, fallback to the full description (optional)
    if (!textContent) {
      textContent = doc.body.textContent?.trim() || '';
    }
  
    // Truncate the text content if it's too long
    if (textContent.length > maxLength) {
      return `${textContent.slice(0, maxLength).trim()}...`;
    }
  
    return textContent;
  };
  
  // Handle locale dynamically
  const handleLinkHover = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, 
    href: string
  ) => {
    const domain = window.location.origin; // Get the domain dynamically
    const locale = window?.location?.pathname.split('/')[1] || 'au'; // Get the locale dynamically
    const updatedHref = `${domain}/${locale}${href.startsWith('/') ? href : `/${href}`}`;
    event.currentTarget.setAttribute('href', updatedHref);
  };

  return (
    <div className="items-start gap-24 pt-24 md:flex md:flex-row lg:px-48 xl:px-60 2xl:gap-48 2xl:px-80 px-24 md:px-28 pb-48 xl:pb-0 2xl:pb-48">
    <div className="w-full">
      {filteredBlogs.length > 0 ? (
        // Determine the appropriate column count dynamically
            <div
            className={`grid ${
                filteredBlogs.length >= 5
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                : `grid-cols-1 sm:grid-cols-2 lg:grid-cols-${filteredBlogs.length}`
            } gap-10`}
            >
          {filteredBlogs.map((blog: any, index: number) => {
            const section = blog.sections?.find(
              (sec: any) => sec.section_with_image_text
            )?.section_with_image_text;
  
            if (!section) {
              return null; // Skip if no section_with_image_text is found
            } 
            const cmsCssClass = section.additional_css || '';
            const modifiedUrl = blog.url;
            const isTitleEmpty = !section.title;
  
            return (
              <div
                key={index}
                className={`block hover:shadow-lg transition-shadow ${cmsCssClass}`}
                style={{ backgroundColor: '#F3EDE9' }}
              >
                <a
                  href={modifiedUrl}
                  className="group"
                >
                 {section.image?.url && (
                      <div className="w-full aspect-[4/3]">
                        <img
                          src={section.image.url}
                          alt={section.image.description || 'Innovation Image'}
                          className={`w-full h-full object-cover rounded-md ${cmsCssClass}`}
                        />
                      </div>

                  )}
                  {section.title && (
                    <div className={`p-2 mt-2 ${cmsCssClass}`}>
                      <Markdown
                        className="text-lg font-semibold text-gray-800"
                        markdown={section.title}                        
                      />
                    </div>
                  )}
                  {section.description && (
                    <div className={`p-2 ${cmsCssClass} ${isTitleEmpty ? 'mt-2' : ''}`}>
                      <Markdown
                        markdown={truncateDescription(section.description)}
                        className="text-sm text-gray-600 p-5"
                      />
                    </div>
                  )}
                  <div className="mt-2 text-right p-2 transform transition-transform duration-300 group-hover:translate-x-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      className="w-32 h-32 text-gray-600"
                    >
                      <path
                        fillRule="evenodd"
                        d="M13.293 4.293a1 1 0 011.414 0l6 6a1 1 0 01.083 1.32l-.083.094-6 6a1 1 0 01-1.497-1.32l.083-.094L17.585 12H4a1 1 0 01-.117-1.993L4 10h13.585l-4.293-4.293a1 1 0 01-.083-1.32z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </a>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-gray-500">No product releases available.</p>
      )}
      <div className="mt-40"></div>
    </div>
  </div>  
  );
};

export default OurTechnologiesTastic;
